// Scale-space globe — web shell (ARCHITECTURE.md §8). The renderer, the camera and the level selection are Rust
// (wasm); this file streams the data in (coarse first), forwards gestures, drives the HUD and exposes
// `window.__globe` for automation. It never draws.
//
// URL params (all optional): lat, lon, s, tilt, heading, mode=continuous|discrete|split, split=<0..1> (divider of the
// split-screen A/B), wire=1, bias, meshpx, freeze=<seconds>, tour=1|0 (a bare URL autoplays the tour), ui=full (the whole HUD; default = only a Play button), ui=0 (hides EVERYTHING: HUD, caption, labels),
// caption=0, maxlevel=<k>;  plus for development: synthetic=1|<size>, stats=1, debug=<view>, dpr, msaa=1, exposure,
// slow=<ms> (delays every level: shows the streaming morph), biomedelay=<ms> (delays the biome: shows ITS morph),
// budget=<megapixels> (canvas pixel budget, default 2.8), governor=0 (never lower the resolution under load),
// loop=1 (the tour flies again and again; by default it ends after one lap), labels=1 (with ui=0 and mode=split: keep the
// two labels of the split screen — a recording must say which half is which).
// Keys: Space tour · D continuous ↔ discrete (split → discrete) · W wireframe · S split screen (when the renderer has it) · I caption ·
// U hide / show the whole UI · H home · + − zoom · arrows pan.

interface Engine {
  expectLevels(field: string, levels: Uint32Array): void;
  setReliefMax(k: number, metres: number): void;
  uploadLevel(field: string, k: number, width: number, height: number, data: Uint8Array): void;
  uploadBiome(width: number, height: number, data: Uint8Array): void;
  giveUpLevel(field: string, k: number): void;
  setDataSettled(settled: boolean): void;
  syntheticLevel(k: number, size: number): void;
  setView(json: string): void;
  setMode(mode: string): void;
  setWire(wire: boolean): void;
  setSplit(x: number): void;
  setOptions(json: string): void;
  drag(dx: number, dy: number, nowMs: number): void;
  dragEnd(nowMs: number): void;
  orbit(dx: number, dy: number): void;
  zoom(ds: number, ndcX: number, ndcY: number): void;
  home(): void;
  tour(on: boolean): void;
  resize(cssW: number, cssH: number, dpr: number): void;
  render(nowMs: number): string;
  stats(): string;
  frameDone(): Promise<void>;
  stop(): void;
}
interface Stats {
  fps: number; gpu_ms: number | null; nodes: number; triangles: number; s_center: number; s_loaded: number; levels_loaded: number;
  ready: boolean; error: string | null; diag: string[]; frame_ms: number; cpu_ms: number; size: [number, number]; dpr: number;
  mode: string; split: number; split_supported: boolean; wire: boolean; mesh_px: number; mesh_px_active: number; mesh_px_device: number;
  funnel: boolean; sigma_km: number; tour: number | null; tour_seconds: number; adapter: string;
  view: { lat: number; lon: number; s: number; tilt: number; heading: number; distance: number; s_frame: number };
  levels: { coast: number[]; relief: number[]; biome: boolean; biome_mix: number; s_loaded_coast: number; s_loaded_relief: number };
  select: { visited: number; culled_horizon: number; culled_frustum: number; max_depth: number };
}
interface View { lat?: number; lon?: number; s?: number; tilt?: number; heading?: number; animate?: boolean }
interface ManifestLevel { k: number; file: string; width: number; height: number; sigma_km?: number; source?: string; relief_max_m?: number }
interface Manifest { version: number; levels: ManifestLevel[]; relief_max_m?: number[]; biome?: { file: string; width: number; height: number } }
declare global {
  interface Window {
    __globe: {
      ready: boolean;
      stats(): Stats;
      setView(v: View): void;
      setMode(m: 'continuous' | 'discrete' | 'split'): void;
      setWire(b: boolean): void;
      setSplit(x: number): void;
      engine?: Engine;
      frameDone?(): Promise<void>;
      /** canvas resolution: the pixel budget, the governor's state and the ratio in use (QA) */
      resolution?(): { budget: number; scale: number; steps: number; gpu_ms: number; frame_ms: number; dropped: number; dpr: number; pinned: boolean };
      errors: string[];
    };
  }
}

const params = new URLSearchParams(location.search);
const $ = <T extends HTMLElement>(id: string): T => document.getElementById(id) as T;
const canvas = $<HTMLCanvasElement>('world');
const loading = $('loading');
const loadingBar = $('loading-bar');
const loadingStatus = $('loading-status');
const statsEl = $('stats');
const errors: string[] = [];

const num = (key: string): number | null => {
  const v = params.get(key);
  if (v === null || v.trim() === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};
const on = (key: string): boolean => params.has(key) && !['0', 'off', 'false', 'no'].includes((params.get(key) ?? '').toLowerCase());

// Three levels of chrome. The PUBLISHED page (no `ui` param) shows nothing but a Play button and plays the tour on launch;
// ui=full (or 1) is the whole HUD — gauge, A/B, wireframe, caption — for QA and explanatory recordings; ui=0 is nothing at all.
const uiParam = (params.get('ui') ?? '').toLowerCase();
const uiLevel: 'none' | 'play' | 'full' = uiParam === '0' ? 'none' : uiParam === 'full' || uiParam === '1' ? 'full' : 'play';
const showUi = uiLevel === 'full';
const showStats = on('stats');
const freeze = num('freeze');
const maxLevel = Math.max(0, Math.min(8, Math.round(num('maxlevel') ?? 8)));
const slowMs = Math.max(0, num('slow') ?? 0);
const synthetic = on('synthetic');
if (uiLevel === 'none') document.body.classList.add('no-ui');
if (uiLevel === 'play') document.body.classList.add('ui-play');
if (params.get('caption') === '0') document.body.classList.add('no-caption');
if (on('labels')) document.body.classList.add('keep-labels'); // ?ui=0&labels=1: a recording of the split screen keeps its two labels
if (freeze !== null) document.body.classList.add('frozen'); // QA shots: no fades
// Autoplay: a bare link flies the tour as soon as the globe is revealed. Not when the URL names a view (a deep link lands where it
// says), not under a frozen clock (QA shots), not with tour=0, not for a visitor who asked for reduced motion; tour=1 always plays.
const viewNamed = ['s', 'lat', 'lon', 'tilt', 'heading'].some((key) => num(key) !== null);
const autoplay = params.has('tour') ? on('tour') : !viewNamed && freeze === null && !window.matchMedia('(prefers-reduced-motion: reduce)').matches;

function progress(text: string, fraction: number): void {
  loadingStatus.textContent = text;
  loadingBar.style.transform = `scaleX(${Math.min(1, Math.max(0, fraction))})`;
}
function fail(message: string): void {
  errors.push(message);
  loading.classList.remove('done');
  loading.classList.add('error');
  loading.setAttribute('aria-busy', 'false');
  loadingStatus.textContent = message;
  console.error(`[globe] ${message}`);
}

// the automation contract exists from the first moment (ready: false) so a waiting script never sees `undefined`
let engineRef: Engine | null = null;
let lastStats: Stats | null = null;
let readyFlag = false;
const pendingCalls: Array<(e: Engine) => void> = [];
const withEngine = (fn: (e: Engine) => void): void => { if (engineRef) fn(engineRef); else pendingCalls.push(fn); };
window.__globe = {
  get ready() { return readyFlag; },
  stats: () => (engineRef ? (JSON.parse(engineRef.stats()) as Stats) : ({ fps: 0, nodes: 0, triangles: 0, s_center: 0, s_loaded: -1, levels_loaded: 0, ready: false } as unknown as Stats)),
  setView: (v: View) => withEngine((e) => e.setView(JSON.stringify(v ?? {}))),
  setMode: (m) => withEngine((e) => { e.setMode(m); syncHud(); }),
  setWire: (b) => withEngine((e) => { e.setWire(Boolean(b)); syncHud(); }),
  setSplit: (x) => withEngine((e) => { e.setSplit(Number(x)); syncHud(); }),
  errors,
} as Window['__globe'];

/* ------------------------------------------------------------------ canvas resolution
 * The cost of a frame is its FRAGMENTS (the mesh is ≈ 1 ms): measured on an M5 Max, a 1600×1000 window at dpr 2 (6.4 MP,
 * 4× MSAA) ran the globe-to-region zoom at 31 – 50 fps (23 ms at s 0.9 even with the 6 px mesh; 14.9 ms at 3.2 MP, 13.5 ms at 2.6 MP).
 * So the canvas is sized by a PIXEL BUDGET, not by the display:
 * dpr = min(cap, devicePixelRatio, √(budget / CSS pixels)) — a retina laptop window renders at ≈ 1.4 – 1.5 (σ and the mesh
 * are sized in CSS px, §4, so the MAP is identical; only the sampling density changes) — and a GOVERNOR lowers the budget
 * step by step while the page really MISSES 60 fps because of the GPU (one way: a resolution that flips back and forth
 * would itself be a pop). "Really misses": until the experience fix the trigger was the smoothed GPU time alone (> 15 ms),
 * and on the very machine the budget was tuned on the continuous globe (s 0.2 – 0.7: 14.2 – 15.7 ms) sat ON that trigger —
 * an ordinary first zoom lost 18 % of its pixels for the rest of the session in 4 visits out of 5 without one dropped frame.
 * Now a step needs GOVERNOR_PATIENCE consecutive frames of either
 *   · a GPU that cannot make a 60 Hz frame on its own (smoothed GPU time > 17 ms), or
 *   · — once the data is in: uploads hitch too — frames that ARE being dropped (more than ≈ 1 in 14 intervals > 24 ms, or a
 *     smoothed interval > 20 ms) while the GPU is what fills them (> 11 ms; a main-thread hitch is not cured by fewer pixels).
 * `?dpr=` pins the ratio and switches both off (every QA script does). */
const PIXEL_BUDGET = Math.max(0.25, num('budget') ?? 2.8) * 1e6;
const GOVERNOR_STEP = 0.82;          // each step keeps this share of the pixels …
const GOVERNOR_FLOOR = 0.25;         // … down to this share of the budget (0.8 MP)
const GOVERNOR_GPU_MS = 15.0;        // the GPU time a step aims below (it sizes the step; the compositor needs the rest of the 16.7)
const GOVERNOR_GPU_OVER_MS = 17.0;   // smoothed GPU time that cannot hold 60 fps whatever the compositor does
const GOVERNOR_GPU_BUSY_MS = 11.0;   // dropped frames are the GPU's doing when its smoothed time is above this
const GOVERNOR_DROPPED_MS = 24;      // a frame interval longer than this is a dropped frame (33 ms at 60 Hz, 25 ms at 120 Hz)
const GOVERNOR_DROP_RATE = 0.06;     // smoothed share of dropped frames that means "missing 60 fps" (≈ 1 in 14; one hitch decays in 12 frames)
const GOVERNOR_SLOW_MS = 20.0;       // … or a smoothed interval above this (a 90 Hz display stuck at 45 fps drops no "frame" by the rule above)
const GOVERNOR_FRAME_MS = 19.5;      // without GPU timestamps (Safari): smoothed frame interval that means "missed 60 fps"
const GOVERNOR_PATIENCE = 45;        // consecutive slow frames before a step
const GOVERNOR_COOLDOWN = 90;        // frames ignored after a step (the resize itself hitches)
const governor = { scale: 1, slow: 0, cooldown: 120, gpu: 0, frame: 0, dropped: 0, steps: 0 };
const dprPinned = (): boolean => { const f = num('dpr'); return f !== null && f > 0; };

function renderDpr(): number {
  const forced = num('dpr');
  if (forced !== null && forced > 0) return Math.min(4, Math.max(0.5, forced));
  const coarse = matchMedia('(pointer: coarse), (hover: none)').matches;
  const cap = Math.min(coarse ? 1.5 : 2, window.devicePixelRatio || 1);
  const css = Math.max(1, window.innerWidth) * Math.max(1, window.innerHeight);
  // (never below half the display's own ratio or 0.75: past that the picture is visibly soft and the budget was the wrong tool)
  const floor = Math.min(cap, Math.max(0.75, 0.5 * cap));
  return Math.max(floor * Math.sqrt(governor.scale), Math.min(cap, Math.sqrt((PIXEL_BUDGET * governor.scale) / css)));
}

/** One frame's timings → maybe one step down. Returns true when the canvas has to be re-sized. */
function govern(s: Stats): boolean {
  if (dprPinned() || freeze !== null || params.get('governor') === '0' || document.hidden) return false;
  if (governor.cooldown > 0) { governor.cooldown--; governor.slow = 0; governor.gpu = 0; governor.frame = 0; governor.dropped = 0; return false; }
  const a = 0.12;
  const timed = s.gpu_ms != null && s.gpu_ms > 0;
  if (timed) governor.gpu = governor.gpu > 0 ? governor.gpu + a * ((s.gpu_ms as number) - governor.gpu) : (s.gpu_ms as number);
  if (s.frame_ms > 0 && s.frame_ms < 250) {
    // (one long frame — a texture upload, a GC pause — counts as ONE dropped frame, not as three: it is not a trend)
    const interval = Math.min(s.frame_ms, 34);
    governor.frame = governor.frame > 0 ? governor.frame + a * (interval - governor.frame) : interval;
    governor.dropped += 0.03 * ((s.frame_ms > GOVERNOR_DROPPED_MS ? 1 : 0) - governor.dropped);
  }
  // (frame intervals also hitch while levels are still being uploaded: they only count once the page is `ready`)
  const dropping = readyFlag && (governor.dropped > GOVERNOR_DROP_RATE || governor.frame > GOVERNOR_SLOW_MS);
  const slow = timed ? governor.gpu > GOVERNOR_GPU_OVER_MS || (dropping && governor.gpu > GOVERNOR_GPU_BUSY_MS) : readyFlag && governor.frame > GOVERNOR_FRAME_MS;
  governor.slow = slow ? governor.slow + 1 : 0;
  if (governor.slow < GOVERNOR_PATIENCE || governor.scale <= GOVERNOR_FLOOR) return false;
  // (a bigger step when it is far off: 28 ms needs half the pixels, not 18 % fewer)
  const over = timed ? Math.max(governor.gpu / GOVERNOR_GPU_MS, governor.frame / 16.7) : governor.frame / GOVERNOR_FRAME_MS;
  governor.scale = Math.max(GOVERNOR_FLOOR, governor.scale * Math.min(GOVERNOR_STEP, Math.max(0.55, 1 / over)));
  governor.steps++;
  governor.slow = 0;
  governor.cooldown = GOVERNOR_COOLDOWN;
  return true;
}

/* ------------------------------------------------------------------ data streaming (coarse first) */
const sleep = (ms: number): Promise<void> => new Promise((r) => setTimeout(r, ms));
const dataUrl = (file: string): string => `${import.meta.env.BASE_URL}data/${file}`;
const loadState = { total: 0, done: 0, firstLevel: false, firstLevelAt: 0, biomeSettled: false };
const biomeDelayMs = Math.max(0, num('biomedelay') ?? 0);
/** The overlay waits this long for the biome after the first coast level is on screen; past it the globe is revealed in
 *  its default greens and the biome MORPHS in when it lands (lib.rs::BiomeMorph) — never a pop either way. */
const BIOME_WAIT_MS = 1500;
/** A deep link (`?s=8`) is revealed once the data is within reach of the view — `s_loaded ≥ min(s, DEEP_LEVEL)` — or after
 *  this long: revealed on level 0 alone it showed seconds of flat green with a hard round edge before it became a map. */
const DEEP_LEVEL = 3;
const DEEP_WAIT_MS = 2500;

async function fetchJson(file: string): Promise<Manifest | null> {
  try {
    const res = await fetch(dataUrl(file), { cache: 'no-cache' });
    if (!res.ok) return null;
    const text = await res.text();
    const json = JSON.parse(text) as Manifest; // the dev server answers a missing file with index.html: not JSON
    return Array.isArray(json.levels) ? json : null;
  } catch {
    return null;
  }
}

async function fetchBytes(file: string, expected: number): Promise<Uint8Array | null> {
  for (let attempt = 0; attempt < 4; attempt++) {
    try {
      const res = await fetch(dataUrl(file), { cache: 'no-cache' });
      if (res.ok) {
        const buf = new Uint8Array(await res.arrayBuffer());
        if (buf.byteLength === expected) return buf;
        console.warn(`[globe] ${file}: ${buf.byteLength} bytes, expected ${expected} (a baker is probably rewriting it)`);
      }
    } catch { /* retry */ }
    await sleep(1200 * (attempt + 1));
  }
  return null;
}

/** Loads one field's levels in order. Resolves when every listed level (≤ maxlevel) was attempted. */
async function loadField(engine: Engine, field: 'coast' | 'relief', manifest: Manifest): Promise<void> {
  const levels = manifest.levels.filter((l) => Number.isInteger(l.k) && l.k >= 0 && l.k <= maxLevel && l.width > 0 && l.height > 0).sort((a, b) => a.k - b.k);
  engine.expectLevels(field, new Uint32Array(levels.map((l) => l.k)));
  loadState.total += levels.length;
  if (field === 'relief') {
    for (const l of levels) {
      const max = l.relief_max_m ?? manifest.relief_max_m?.[l.k];
      if (typeof max === 'number' && max > 0) engine.setReliefMax(l.k, max);
    }
  }
  for (const l of levels) {
    if (slowMs) await sleep(slowMs);
    const bytes = await fetchBytes(l.file, l.width * l.height);
    try {
      if (!bytes) throw new Error(`${l.file} could not be loaded`);
      engine.uploadLevel(field, l.k, l.width, l.height, bytes);
    } catch (e) {
      console.warn(`[globe] ${field} level ${l.k} skipped: ${e instanceof Error ? e.message : String(e)}`);
      engine.giveUpLevel(field, l.k);
    }
    loadState.done++;
    if (l.k === 0 && field === 'coast') { loadState.firstLevel = true; loadState.firstLevelAt = performance.now(); }
  }
}

/** The biome is the largest early file (2.2 MB compressed against 0.8 MB for coast L0): it is requested FIRST, in parallel
 *  with both level chains — behind relief L0 it always landed after the reveal. */
async function loadBiome(engine: Engine, b: NonNullable<Manifest['biome']>): Promise<void> {
  try {
    if (biomeDelayMs) await sleep(biomeDelayMs);
    const rgba = await fetchBytes(b.file, b.width * b.height * 4);
    if (rgba) engine.uploadBiome(b.width, b.height, rgba);
  } catch (e) {
    console.warn(`[globe] biome skipped: ${e instanceof Error ? e.message : String(e)}`);
  } finally {
    loadState.biomeSettled = true;
  }
}

async function loadSynthetic(engine: Engine): Promise<void> {
  const size = Math.max(64, num('synthetic') ?? 0) > 64 ? Number(num('synthetic')) : 512;
  const ks = Array.from({ length: maxLevel + 1 }, (_, k) => k);
  engine.expectLevels('coast', new Uint32Array(ks));
  engine.expectLevels('relief', new Uint32Array(ks));
  loadState.total += ks.length * 2;
  for (const k of ks) {
    if (slowMs) await sleep(slowMs);
    await new Promise((r) => requestAnimationFrame(() => setTimeout(r, 0)));
    engine.syntheticLevel(k, size);
    loadState.done += 2;
    if (k === 0) loadState.firstLevel = true;
  }
}

/** Both manifests, polled until they exist (the bakers may still be running); the relief never blocks `ready`
 *  if its manifest is not there on the first try — it is picked up and streamed in whenever it appears. */
async function loadData(engine: Engine): Promise<void> {
  if (synthetic) {
    await loadSynthetic(engine);
    loadState.biomeSettled = true;
    engine.setDataSettled(true);
    return;
  }
  const poll = async (file: string, firstTryOnly: boolean): Promise<Manifest | null> => {
    for (let i = 0; ; i++) {
      const m = await fetchJson(file);
      if (m || firstTryOnly) return m;
      if (i === 0) progress('Waiting for the map data (bakers still running?)…', 0.3);
      await sleep(2500);
    }
  };
  const reliefNow = await poll('manifest.relief.json', true);
  const biomeJob = reliefNow?.biome ? loadBiome(engine, reliefNow.biome) : null;
  if (!biomeJob) loadState.biomeSettled = true; // (no relief manifest yet: nothing to wait for — a late one morphs in)
  const reliefJob = reliefNow ? loadField(engine, 'relief', reliefNow) : null;
  const coast = await poll('manifest.coast.json', false);
  const coastJob = loadField(engine, 'coast', coast as Manifest);
  await Promise.all([coastJob, reliefJob, biomeJob]);
  engine.setDataSettled(true);
  if (!reliefJob) {
    const late = await poll('manifest.relief.json', false);
    if (late) {
      const lateBiome = late.biome ? loadBiome(engine, late.biome) : null;
      await Promise.all([loadField(engine, 'relief', late), lateBiome]);
    }
  }
}

/* ------------------------------------------------------------------ HUD */
const gaugeS = $('gauge-s');
const gaugeSigma = $('gauge-sigma');
const gaugePlace = $('gauge-place');
const gaugeFill = $('gauge-fill');
const gaugeLoaded = $('gauge-loaded');
const gaugeThumb = $('gauge-thumb');
const gaugeTrack = $('gauge-track');
const btnContinuous = $<HTMLButtonElement>('mode-continuous');
const btnDiscrete = $<HTMLButtonElement>('mode-discrete');
const btnSplit = $<HTMLButtonElement>('mode-split');
const btnWire = $<HTMLButtonElement>('toggle-wire');
const btnTour = $<HTMLButtonElement>('toggle-tour');
const meshNote = $('mesh-note');
const splitDivider = $('split-divider');

function sigmaText(km: number): string {
  if (km >= 10) return `${km.toFixed(1)} km`;
  if (km >= 1) return `${km.toFixed(2)} km`;
  return `${Math.round(km * 1000)} m`;
}

/** Where we are, in words: the finest name whose scale has been reached AND whose place the view is centred on. */
const PLACES: Array<{ name: string; s: number; lat: number; lon: number; km: number }> = [
  { name: 'Rade de Brest', s: 7.0, lat: 48.3, lon: -4.45, km: 22 },
  { name: 'Finistère', s: 5.6, lat: 48.25, lon: -4.1, km: 80 },
  { name: 'Brittany', s: 3.4, lat: 48.2, lon: -3.0, km: 270 },
  { name: 'Europe', s: 1.0, lat: 50, lon: 8, km: 2600 },
];
function placeName(s: number, lat: number, lon: number): string {
  const rad = Math.PI / 180;
  const km = (p: { lat: number; lon: number }): number => {
    const c = Math.sin(lat * rad) * Math.sin(p.lat * rad) + Math.cos(lat * rad) * Math.cos(p.lat * rad) * Math.cos((lon - p.lon) * rad);
    return Math.acos(Math.min(1, Math.max(-1, c))) * 6371;
  };
  return PLACES.find((p) => s >= p.s && km(p) <= p.km)?.name ?? 'Earth';
}
let touringShown = false;
let shownPlace = 'Earth';
let placeTimer = 0;
function showPlace(name: string): void {
  if (name === shownPlace) return;
  shownPlace = name;
  if (freeze !== null) { gaugePlace.textContent = name; return; }
  gaugePlace.classList.add('swap');
  clearTimeout(placeTimer);
  placeTimer = window.setTimeout(() => { gaugePlace.textContent = name; gaugePlace.classList.remove('swap'); }, 240);
}

function syncHud(): void {
  if (!engineRef) return;
  const s = JSON.parse(engineRef.stats()) as Stats;
  document.body.classList.toggle('discrete', s.mode === 'discrete');
  document.body.classList.toggle('split', s.mode === 'split');
  document.body.classList.toggle('wire', s.wire);
  document.body.style.setProperty('--split', String(s.split ?? 0.5));
  btnContinuous.setAttribute('aria-pressed', String(s.mode === 'continuous'));
  btnDiscrete.setAttribute('aria-pressed', String(s.mode === 'discrete'));
  btnSplit.setAttribute('aria-pressed', String(s.mode === 'split'));
  // the split-screen control appears once the renderer can draw it (or when the URL asks for it)
  btnSplit.hidden = !(s.split_supported || s.mode === 'split');
  btnWire.setAttribute('aria-pressed', String(s.wire));
  btnTour.setAttribute('aria-pressed', String(s.tour !== null));
  if (s.mode === 'split') layoutSplitLabels(s.split ?? 0.5);
}

/** The two labels of the split screen hang off the divider. When their half gets narrower than they are they first drop
 *  their sub-line, then go — they used to run off the screen (divider at 86 %: "DISCRETE ZOOM LEVELS — TODAY'S MAPS" cut). */
const splitLabels = [$('split-left'), $('split-right')];
function layoutSplitLabels(split: number): void {
  const w = Math.max(1, window.innerWidth);
  const room = [split * w, (1 - split) * w].map((half) => half - 22 - 12);
  splitLabels.forEach((label, i) => {
    label.classList.remove('compact', 'gone');
    if (label.offsetWidth > room[i]) label.classList.add('compact');
    if (label.offsetWidth > room[i]) label.classList.add('gone');
  });
}

function updateGauge(s: Stats): void {
  // the level actually drawn at the screen centre: continuous, or snapped in discrete mode
  // (the framed globe sits below level 0, s ≈ −0.3: the gauge's track starts at 0, and so does its number)
  const clamped = Math.min(8, Math.max(0, s.s_center));
  const shown = s.mode === 'discrete' ? Math.round(clamped) : clamped;
  gaugeS.textContent = shown.toFixed(2);
  // σ of the picture, not of the wish: while levels are still streaming in (or with ?maxlevel) the data is coarser than the
  // view asks for — the readout says so, dimmed, instead of claiming 125 m over a 4 km blur
  const drawn = Math.min(shown, Math.max(0, s.s_loaded));
  gaugeSigma.textContent = sigmaText(32 / 2 ** drawn);
  gaugeSigma.parentElement?.classList.toggle('lagging', shown - drawn > 0.25);
  const pct = (shown / 8) * 100;
  gaugeFill.style.width = `${pct}%`;
  gaugeThumb.style.left = `${pct}%`;
  gaugeLoaded.style.width = `${(Math.min(8, Math.max(0, s.s_loaded)) / 8) * 100}%`;
  showPlace(placeName(s.s_center, s.view.lat, s.view.lon));
  const touring = s.tour !== null;
  if (btnTour.getAttribute('aria-pressed') !== String(touring)) btnTour.setAttribute('aria-pressed', String(touring));
  btnTour.style.setProperty('--tour', touring && s.tour_seconds > 0 ? ((s.tour as number) / s.tour_seconds).toFixed(4) : '0');
  if (s.wire) {
    // the mesh the shaded view uses, too: requested cell size, and what the tilt / the quadtree make of it
    const active = s.mesh_px_active;
    // (+ a legend: without one the per-level hues read as a glitch — a "stepped orange rectangle over Brittany")
    const cell = Math.abs(active - s.mesh_px) < 0.05 ? `cell ${s.mesh_px.toFixed(1)} px` : `cell ${s.mesh_px.toFixed(1)} → ${active.toFixed(1)} px`;
    const note = `${cell} · hue = quadtree level · faint = morphing`;
    if (meshNote.textContent !== note) meshNote.textContent = note;
  }
}

/* ------------------------------------------------------------------ boot */
async function boot(): Promise<void> {
  if (!('gpu' in navigator)) {
    fail('This globe is drawn live with WebGPU, which this browser does not offer yet. It runs in Chrome, Edge, Arc, and Safari 26 or newer.');
    return;
  }
  progress('Fetching the renderer…', 0.1);
  const wasm = await import(/* @vite-ignore */ `${import.meta.env.BASE_URL}wasm/globe_web.js`);
  await wasm.default();
  progress('Starting WebGPU…', 0.2);

  const dpr = renderDpr();
  const options = {
    dpr,
    msaaSamples: params.get('msaa') === '1' ? 1 : 4,
    timestamps: params.get('timestamps') !== '0',
    freeze,
    lat: num('lat'),
    lon: num('lon'),
    s: num('s'),
    tilt: num('tilt'),
    heading: num('heading'),
    mode: ['discrete', 'split'].includes(params.get('mode') ?? '') ? params.get('mode') : 'continuous',
    split: Math.min(1, Math.max(0, num('split') ?? 0.5)),
    wire: on('wire'),
    bias: num('bias') ?? 0,
    // (null = the engine's DEFAULT_MESH_PX, 6 CSS px, on every device: the picture does not depend on the mesh, and small
    // triangles make the heavy fragment shader run more than once per pixel — see lib.rs)
    meshpx: num('meshpx'),
    tour: autoplay,
    tourLoop: on('loop'),
    // the loading overlay covers the canvas until the reveal (frame() below): until then the engine animates nothing for
    // nobody — `s_loaded` and the biome snap to what has arrived, the tour's clock waits (lib.rs Options::hidden)
    hidden: true,
    debug: params.get('debug') ?? '',
    exposure: num('exposure') ?? 1,
  };
  let engine: Engine;
  try {
    engine = (await wasm.initGlobe(canvas, JSON.stringify(options))) as Engine;
  } catch (e) {
    // (the raw error is wgpu's adapter report — backend names, "noop not requested": for the console, not for a visitor)
    console.error('[globe] initGlobe failed:', e);
    const raw = e instanceof Error ? e.message : String(e);
    fail(/adapter/i.test(raw)
      ? 'This browser has WebGPU, but no graphics adapter could be opened. Turn on hardware acceleration (in Chrome: Settings → System → “Use graphics acceleration when available”), restart the browser, or try another one.'
      : 'WebGPU could not start on this device. Update the browser and the graphics driver, or try Chrome, Edge or Safari 26.');
    return;
  }
  engineRef = engine;
  window.__globe.engine = engine;
  window.__globe.frameDone = () => engine.frameDone();
  window.__globe.resolution = () => ({ budget: PIXEL_BUDGET, scale: governor.scale, steps: governor.steps, gpu_ms: governor.gpu, frame_ms: governor.frame, dropped: governor.dropped, dpr: renderDpr(), pinned: dprPinned() });

  let cssW = 1;
  let cssH = 1;
  const resize = (): void => {
    cssW = Math.max(1, window.innerWidth);
    cssH = Math.max(1, window.innerHeight);
    engine.resize(cssW, cssH, renderDpr());
  };
  resize();
  // (re-apply the URL view: `s` defaults to the framed globe, which depends on the viewport just set)
  if (!options.tour) engine.setView(JSON.stringify({ lat: options.lat, lon: options.lon, s: options.s, tilt: options.tilt, heading: options.heading }));
  for (const call of pendingCalls.splice(0)) call(engine);
  window.addEventListener('resize', resize);
  window.addEventListener('resize', syncHud);   // (the split-screen labels are laid out against the window's width)
  window.addEventListener('orientationchange', () => setTimeout(resize, 120));
  window.visualViewport?.addEventListener('resize', resize);
  const watchDpr = (): void => matchMedia(`(resolution: ${window.devicePixelRatio}dppx)`).addEventListener('change', () => { resize(); watchDpr(); }, { once: true });
  watchDpr();

  bindInput(engine, () => ({ cssW, cssH, dpr: renderDpr() }));
  bindHud(engine);
  syncHud();

  progress('Loading the Earth…', 0.35);
  loadData(engine).catch((e) => fail(`The map data could not be loaded (${e instanceof Error ? e.message : String(e)}).`));

  let failed = false;
  let revealed = false;
  let framesAfterFirstLevel = 0;
  let governorResize = false;
  const frame = (now: number): void => {
    if (failed) return;
    requestAnimationFrame(frame);
    // The governor's step is applied BEFORE this frame is drawn, never after the last one: setting canvas.width clears
    // the canvas, so a resize that follows the render hands the compositor an EMPTY frame — every step used to flash one
    // black frame (globe and stars gone, HUD still up). A window resize is safe for the same reason: its event runs
    // before the frame's rAF callbacks.
    if (governorResize) { governorResize = false; resize(); }
    const s = JSON.parse(engine.render(now)) as Stats;
    lastStats = s;
    if (s.error) {
      failed = true;
      fail(`Rendering stopped: ${s.error}`);
      return;
    }
    if (showUi) updateGauge(s);
    // (the Play button of the published page is there exactly when there is something to play: revealed, and not already flying)
    if (touringShown !== (s.tour !== null)) { touringShown = s.tour !== null; document.body.classList.toggle('touring', touringShown); }
    if (govern(s)) governorResize = true;
    if (!revealed) {
      progress(loadState.total ? `Loading the Earth… ${loadState.done} / ${loadState.total}` : loadingStatus.textContent ?? '', 0.35 + 0.65 * (loadState.total ? loadState.done / loadState.total : 0));
      // the overlay lifts as soon as the coarsest level AND the biome are on screen (the biome is given BIOME_WAIT_MS: past
      // that it morphs in under the visitor's eyes); finer levels stream in and MORPH in
      const biomeIn = loadState.biomeSettled || performance.now() - loadState.firstLevelAt > BIOME_WAIT_MS;
      // Level 0 must be IN, not fading in: the fade scales the land mask, so at s_loaded = −0.5 the 0.5 contour — every
      // coast of the planet — appeared in one frame, 0.5 s after the overlay had begun to lift (measured: 42 % of the
      // globe's pixels in one frame pair). While `hidden` the engine snaps s_loaded, so this costs no time.
      const coastIn = s.levels.s_loaded_coast >= 0;
      const dataIn = s.s_loaded >= Math.min(DEEP_LEVEL, maxLevel, s.s_center) - 1e-3 || performance.now() - loadState.firstLevelAt > DEEP_WAIT_MS;
      if (loadState.firstLevel && coastIn && biomeIn && dataIn && ++framesAfterFirstLevel >= 3) {
        revealed = true;
        engine.setOptions(JSON.stringify({ hidden: false }));   // from here on everything that arrives MORPHS in; the tour's clock starts
        loading.classList.add('done');
        document.body.classList.add('revealed');
        loading.setAttribute('aria-busy', 'false');
      }
    }
    // (`ready` includes "the globe is revealed": under the overlay the engine snaps s_loaded, so with few levels — ?maxlevel —
    // its own `ready` can come a frame BEFORE the reveal, and a script that shoots at `ready` would shoot the overlay)
    readyFlag = s.ready && revealed && errors.length === 0;
    if (showStats) {
      statsEl.hidden = false;
      statsEl.textContent =
        `${s.fps.toFixed(0)} fps  frame ${s.frame_ms.toFixed(1)} ms  cpu ${s.cpu_ms.toFixed(2)} ms` + (s.gpu_ms != null ? `  gpu ${s.gpu_ms.toFixed(2)} ms` : '') +
        `\n${s.size[0]}×${s.size[1]} @${s.dpr.toFixed(2)}${governor.steps ? ` (governor ×${governor.scale.toFixed(2)})` : ''}  ${s.nodes} patches (depth ≤ ${s.select.max_depth})  ${(s.triangles / 1e3).toFixed(0)} k tris  mesh ${s.mesh_px} → ${s.mesh_px_active.toFixed(2)} css px (${s.mesh_px_device.toFixed(2)} device)` +
        `\ns ${s.s_center.toFixed(3)}  σ ${sigmaText(s.sigma_km)}  loaded ${s.levels.s_loaded_coast.toFixed(2)} / ${s.levels.s_loaded_relief.toFixed(2)}  [${s.levels.coast.join('')}] [${s.levels.relief.join('')}]` +
        `\nlat ${s.view.lat.toFixed(3)}  lon ${s.view.lon.toFixed(3)}  tilt ${s.view.tilt.toFixed(1)}°  heading ${s.view.heading.toFixed(1)}°  ${s.mode}${s.funnel ? '  funnel' : ''}`;
    }
  };
  requestAnimationFrame(frame);
}

/* ------------------------------------------------------------------ gestures */
function bindInput(engine: Engine, size: () => { cssW: number; cssH: number; dpr: number }): void {
  const pointers = new Map<number, { x: number; y: number; orbit: boolean }>();
  let pinch: { dist: number; cx: number; cy: number; angle: number; twist: number } | null = null;
  let pinched = false; // this gesture had two fingers down at some point: it must not end in a fling
  const pinchOf = (twist: number): NonNullable<typeof pinch> => {
    const [a, b] = [...pointers.values()];
    return { dist: Math.hypot(a.x - b.x, a.y - b.y), cx: (a.x + b.x) / 2, cy: (a.y + b.y) / 2, angle: Math.atan2(b.y - a.y, b.x - a.x), twist };
  };
  const ndc = (x: number, y: number): [number, number] => {
    const { cssW, cssH } = size();
    return [(x / cssW) * 2 - 1, 1 - (y / cssH) * 2];
  };
  canvas.addEventListener('contextmenu', (e) => e.preventDefault());
  canvas.addEventListener('pointerdown', (e) => {
    canvas.setPointerCapture(e.pointerId);
    canvas.focus({ preventScroll: true });
    pointers.set(e.pointerId, { x: e.clientX, y: e.clientY, orbit: e.button === 2 || e.button === 1 || e.ctrlKey || e.shiftKey || e.altKey });
    canvas.classList.add('dragging');
    // the pinch starts where the second finger lands (no motion is lost to initialising it on the first move)
    pinch = pointers.size === 2 ? pinchOf(0) : null;
    if (pointers.size >= 2) pinched = true;
  });
  canvas.addEventListener('pointermove', (e) => {
    const p = pointers.get(e.pointerId);
    if (!p) return;
    const { dpr } = size();
    const [dx, dy] = [e.clientX - p.x, e.clientY - p.y];
    p.x = e.clientX;
    p.y = e.clientY;
    if (pointers.size === 1) {
      if (p.orbit) engine.orbit(dx * dpr, dy * dpr);
      else engine.drag(dx * dpr, dy * dpr, performance.now());
      return;
    }
    if (pointers.size === 2) {
      const { dist, cx, cy, angle } = pinchOf(0);
      let twist = pinch?.twist ?? 0;
      if (pinch && dist > 8 && pinch.dist > 8) {
        const [nx, ny] = ndc(cx, cy);
        engine.zoom(Math.log2(dist / pinch.dist), nx, ny);
        engine.drag((cx - pinch.cx) * dpr, (cy - pinch.cy) * dpr, performance.now());
        // two-finger twist turns the map — once it is clearly a twist (12°), so a plain pinch never rotates
        let da = angle - pinch.angle;
        da = Math.atan2(Math.sin(da), Math.cos(da));
        const before = twist;
        twist += da;
        if (Math.abs(twist) > 0.21) engine.orbit(((Math.abs(before) > 0.21 ? da : twist - Math.sign(twist) * 0.21) * (180 / Math.PI) / 0.25) * dpr, 0);
      }
      pinch = { dist, cx, cy, angle, twist };
    }
  });
  const release = (e: PointerEvent): void => {
    if (!pointers.delete(e.pointerId)) return;
    pinch = null;
    if (pointers.size === 0) {
      canvas.classList.remove('dragging');
      // (two fingers never move in step: the pinch centre jitters, and its last jitter must not become a fling)
      engine.dragEnd(performance.now() + (pinched ? 1000 : 0));
      pinched = false;
    }
  };
  canvas.addEventListener('pointerup', release);
  canvas.addEventListener('pointercancel', release);
  // (on the window: a wheel over the HUD or the caption zooms too — a recording cursor often rests there)
  window.addEventListener('wheel', (e) => {
    e.preventDefault();
    const unit = e.deltaMode === 1 ? 16 : e.deltaMode === 2 ? 400 : 1;
    // ctrl+wheel is a trackpad pinch: finer deltas, stronger gain
    const ds = -e.deltaY * unit * (e.ctrlKey ? 0.012 : 0.0022);
    const [nx, ny] = ndc(e.clientX, e.clientY);
    engine.zoom(Math.max(-1.2, Math.min(1.2, ds)), nx, ny);
  }, { passive: false });
  canvas.addEventListener('dblclick', (e) => {
    const [nx, ny] = ndc(e.clientX, e.clientY);
    engine.zoom(e.shiftKey ? -1 : 1, nx, ny);
  });
  window.addEventListener('keydown', (e) => {
    if (e.metaKey || e.ctrlKey || e.altKey) return;
    if (e.target instanceof HTMLElement && e.target.closest('button') && (e.key === ' ' || e.key === 'Enter')) return; // a focused button handles its own key
    const k = e.key === ' ' ? 'space' : e.key.toLowerCase();
    const { dpr } = size();
    const step = 60 * dpr;
    if (k === '+' || k === '=') engine.zoom(0.5, 0, 0);
    else if (k === '-' || k === '_') engine.zoom(-0.5, 0, 0);
    else if (k === 'arrowleft') { engine.drag(step, 0, performance.now()); engine.dragEnd(performance.now() + 1000); }
    else if (k === 'arrowright') { engine.drag(-step, 0, performance.now()); engine.dragEnd(performance.now() + 1000); }
    else if (k === 'arrowup') { engine.drag(0, step, performance.now()); engine.dragEnd(performance.now() + 1000); }
    else if (k === 'arrowdown') { engine.drag(0, -step, performance.now()); engine.dragEnd(performance.now() + 1000); }
    else if (k === 'h' || k === 'home') engine.home();
    else if (k === 'w') { engine.setWire(!(lastStats?.wire ?? false)); syncHud(); }
    // (D from the split screen goes to the mode it is named after; C always to continuous)
    else if (k === 'd') { engine.setMode(lastStats?.mode === 'discrete' ? 'continuous' : 'discrete'); syncHud(); }
    else if (k === 'c') { engine.setMode('continuous'); syncHud(); }
    else if (k === 's' && lastStats?.split_supported) { engine.setMode(lastStats.mode === 'split' ? 'continuous' : 'split'); syncHud(); }
    else if (k === 'space' || k === 't') { engine.tour(lastStats?.tour == null); syncHud(); }
    else if (k === 'i') document.body.classList.toggle('no-caption');
    else if (k === 'u') document.body.classList.toggle('ui-hidden');
    else return;
    e.preventDefault();
  });
}

function bindHud(engine: Engine): void {
  // the published page's only control (a drag, a wheel or Space interrupts the flight, and the button comes back)
  const play = $<HTMLButtonElement>('play');
  play.addEventListener('click', () => { engine.tour(true); play.blur(); });
  // (buttons give the keyboard back to the page: Space must start the tour, not re-click the last button)
  const click = (b: HTMLElement, fn: () => void): void => b.addEventListener('click', () => { fn(); syncHud(); if (b instanceof HTMLButtonElement) b.blur(); });
  click(btnContinuous, () => engine.setMode('continuous'));
  click(btnDiscrete, () => engine.setMode('discrete'));
  click(btnSplit, () => engine.setMode('split'));
  click(btnWire, () => engine.setWire(btnWire.getAttribute('aria-pressed') !== 'true'));
  click(btnTour, () => engine.tour(btnTour.getAttribute('aria-pressed') !== 'true'));
  click($('caption-hide'), () => document.body.classList.add('no-caption'));
  // the gauge is also a slider: scrub the scale level (a zoom at the screen centre, through the same funnel)
  let scrubbing = false;
  const scrub = (e: PointerEvent): void => {
    const r = gaugeTrack.getBoundingClientRect();
    const s = Math.min(8, Math.max(0, ((e.clientX - r.left) / r.width) * 8));
    const floor = lastStats?.view.s_frame ?? 0;
    engine.setView(JSON.stringify({ s: s <= 0.06 ? Math.min(0, floor) : s, animate: true }));
  };
  gaugeTrack.addEventListener('pointerdown', (e) => { scrubbing = true; gaugeTrack.setPointerCapture(e.pointerId); scrub(e); });
  gaugeTrack.addEventListener('pointermove', (e) => { if (scrubbing) scrub(e); });
  const end = (): void => { scrubbing = false; };
  gaugeTrack.addEventListener('pointerup', end);
  gaugeTrack.addEventListener('pointercancel', end);
  // split-screen divider (mode=split): drag it; the renderer follows `setSplit`
  let dividing = false;
  const divide = (e: PointerEvent): void => { engine.setSplit(Math.min(0.95, Math.max(0.05, e.clientX / Math.max(1, window.innerWidth)))); syncHud(); };
  splitDivider.addEventListener('pointerdown', (e) => { dividing = true; splitDivider.setPointerCapture(e.pointerId); divide(e); });
  splitDivider.addEventListener('pointermove', (e) => { if (dividing) divide(e); });
  const endDivide = (): void => { dividing = false; };
  splitDivider.addEventListener('pointerup', endDivide);
  splitDivider.addEventListener('pointercancel', endDivide);
}

boot().catch((e) => fail(e instanceof Error ? e.message : String(e)));
